/* calculationHelpers is untyped JS (allowJs) — shared with the single-product measurement module. */
import { isSkipValue as isSkipValueRaw, parseGradingValue as parseGradingValueRaw } from '../../../../../../utils/calculationHelpers';
import type { SpecGrading, SpecPomItem } from './types';

/** Grading calculation mode, taken from `userInfo.gradingFormat`. */
export type GradingFormat = 'sample' | 'previous' | string;

/**
 * Typed views over the untyped JS helpers. This `parseGradingValue` assertion declared a
 * `shouldAdd` the implementation did not return, so every read of it below was `undefined` —
 * falsy — and the +/- branches always subtracted, including for sizes right of the base that must
 * add. The assertion is what stopped the compiler reporting it. The helper now genuinely returns
 * the field; keep this signature and the implementation in step (BM-9510).
 */
const isSkipValue = isSkipValueRaw as (value: unknown) => boolean;
const parseGradingValue = parseGradingValueRaw as (grading: unknown, isLeftSide: boolean) => { value: number; shouldAdd: boolean };

/** A measurement value never carries `undefined` in state — coerce it away for exactOptionalPropertyTypes. */
type MeasurementValue = number | string | null;
const asMeasurement = (value: SpecGrading['measurment']): MeasurementValue => value ?? '';

/** Round to 3 decimals, dropping trailing zeros (mirrors `parseFloat(x.toFixed(3))`). */
const round3 = (num: number): number => parseFloat(num.toFixed(3));

/** Sample-mode formatter returns a Number; previous-mode returns a String (matches Calculation.js). */
const formatNumber = (num: number): string => (Number.isNaN(num) ? '' : round3(num).toString());

/** Numeric value of a measurement, treating skip tokens (N/A, -, /) as 0. */
const toNumber = (value: unknown): number => (isSkipValue(value) ? 0 : parseFloat(String(value ?? '')) || 0);

/** Index of the anchor (sample/zero) size, or -1 when none is flagged. */
const findSampleIndex = (gradings: SpecGrading[]): number => gradings.findIndex((grading) => grading.isZero || grading.isSampleSize);

/** Whether a grading row carries an own `grading` property (unstamped rows may not). */
const hasGradingKey = (grading: SpecGrading): boolean => Object.prototype.hasOwnProperty.call(grading, 'grading');

/**
 * Absolute engine (`gradingFormat === 'sample'`): every non-anchor size is derived directly from the
 * single anchor measurement — `measurment = sampleMeasurement ± gradingDelta`. The anchor is left as
 * the user entered it. Ported from `Calculation.js` `calculationSampleSize`.
 */
function calcSample(gradings: SpecGrading[], origGradings: SpecGrading[]): SpecGrading[] {
  const sampleIndex = findSampleIndex(gradings);
  const sampleObj = sampleIndex >= 0 ? gradings[sampleIndex] : undefined;
  const origSampleObj = origGradings.find((grading) => grading.isZero || grading.isSampleSize);
  const sampleMeasurement = toNumber(sampleObj?.measurment);
  const origSampleMeasurement = toNumber(origSampleObj?.measurment);
  const anchorChanged = sampleMeasurement !== origSampleMeasurement;

  if (sampleIndex < 0) return gradings.map((grading) => ({ ...grading }));

  return gradings.map((grading, idx) => {
    const next: SpecGrading = { ...grading };
    if (!hasGradingKey(grading)) {
      next.isUserChanged = anchorChanged || Boolean(grading.isUserChanged);
      return next;
    }
    if (isSkipValue(grading.grading)) {
      next.measurment = asMeasurement(grading.grading);
      return next;
    }
    const isLeftSide = idx < sampleIndex;
    const { value, shouldAdd } = parseGradingValue(grading.grading, isLeftSide);
    next.isUserChanged = anchorChanged || Boolean(grading.isUserChanged);
    if (idx !== sampleIndex) {
      const measurement = shouldAdd ? sampleMeasurement + value : sampleMeasurement - value;
      next.measurment = Number.isNaN(measurement) ? '' : round3(measurement);
    }
    return next;
  });
}

/**
 * Cumulative engine (any other `gradingFormat`): each size derives from the previous one, walking
 * outward from the anchor (running total). Ported from `Calculation.js` `calculatePreviousSize`.
 */
function calcPrevious(gradings: SpecGrading[], origGradings: SpecGrading[]): SpecGrading[] {
  const next = gradings.map((grading) => ({ ...grading }));
  const sampleIndex = findSampleIndex(next);
  const sampleObj = sampleIndex >= 0 ? next[sampleIndex] : undefined;
  const origSampleObj = origGradings.find((grading) => grading.isZero || grading.isSampleSize);
  const sampleMeasurement = toNumber(sampleObj?.measurment);
  const origSampleMeasurement = toNumber(origSampleObj?.measurment);
  const anchorChanged = sampleMeasurement !== origSampleMeasurement;

  if (sampleIndex < 0) return next;

  let current = sampleMeasurement;
  for (let i = sampleIndex - 1; i >= 0; i -= 1) {
    const grading = next[i];
    if (grading && hasGradingKey(grading)) {
      if (isSkipValue(grading.grading)) {
        grading.measurment = asMeasurement(grading.grading);
      } else {
        const { value, shouldAdd } = parseGradingValue(grading.grading, true);
        current = shouldAdd ? current + value : current - value;
        grading.measurment = formatNumber(current);
        grading.isUserChanged = anchorChanged || Boolean(grading.isUserChanged);
      }
    }
  }

  current = sampleMeasurement;
  for (let i = sampleIndex + 1; i < next.length; i += 1) {
    const grading = next[i];
    if (grading && hasGradingKey(grading)) {
      if (isSkipValue(grading.grading)) {
        grading.measurment = asMeasurement(grading.grading);
      } else {
        const { value, shouldAdd } = parseGradingValue(grading.grading, false);
        /**
         * The running total is NOT passed through Math.abs, matching Calculation.js: a cumulative
         * chain that legitimately crosses zero was folded back to positive, reporting 14 where the
         * measurement is -4 and inverting every later step of the walk. BulkEdit and the spec
         * screen read back each other's rows, so the two walks must agree exactly (BM-9510).
         */
        current = shouldAdd ? current + value : current - value;
        grading.measurment = formatNumber(current);
        grading.isUserChanged = anchorChanged || Boolean(grading.isUserChanged);
      }
    }
  }

  if (sampleObj) {
    sampleObj.measurment = formatNumber(sampleMeasurement);
    sampleObj.isUserChanged = anchorChanged || Boolean(sampleObj.isUserChanged);
  }

  return next;
}

/**
 * Recompute a POM row's derived size measurements after an edit, choosing the engine by
 * `gradingFormat`. Every size takes part, including sizes outside the product's own range — those are
 * calculated and displayed like any other, they are only locked against editing. `originalItem` is the
 * pristine (as-loaded) row, used only for the amber `isUserChanged` diff on the anchor measurement.
 * Pure — returns a new item.
 */
export function recalcRow(item: SpecPomItem, originalItem: SpecPomItem | undefined, gradingFormat: GradingFormat): SpecPomItem {
  const gradings = item.gradings ?? [];
  const origGradings = originalItem?.gradings ?? [];
  const nextGradings = gradingFormat === 'sample' ? calcSample(gradings, origGradings) : calcPrevious(gradings, origGradings);
  return { ...item, gradings: nextGradings };
}

/**
 * Ensure a resolvable anchor: if no size carries `isZero`/`isSampleSize`, stamp them onto the size
 * whose name matches `sampleSizeName` (mirrors `reconcileSampleSizeFlags`). Rows that already have a
 * flagged anchor are left untouched so correct backend data is never disturbed. Pure.
 */
export function stampSampleFlags(item: SpecPomItem, sampleSizeName: string | null | undefined): SpecPomItem {
  const gradings = item.gradings ?? [];
  if (!sampleSizeName || gradings.length === 0) return item;
  const alreadyFlagged = gradings.some((grading) => grading.isZero || grading.isSampleSize);
  if (alreadyFlagged) return item;
  const target = sampleSizeName.trim().toLowerCase();
  return {
    ...item,
    gradings: gradings.map((grading) => {
      const isAnchor = grading.sizeName?.trim().toLowerCase() === target;
      return { ...grading, isZero: isAnchor, isSampleSize: isAnchor };
    }),
  };
}
